import { useMemo } from "react";

interface APMLegendProps {
  trackZones: Array<{
    values: number[];
    apm_track_id?: number;
  }>;
  tracks: Array<{
    id: string;
    code: string;
  }>;
  currentIntervalIndex: number;
  times: string[];
}

export function APMLegend({
  trackZones,
  tracks,
  currentIntervalIndex,
  times,
}: APMLegendProps) {
  const legendData = useMemo(() => {
    const currentByTrack: Record<string, number> = {};
    let currentSystemTotal = 0;

    trackZones.forEach((zone) => {
      const value = zone.values[currentIntervalIndex] || 0;
      currentSystemTotal += value;

      if (zone.apm_track_id) {
        const trackId = zone.apm_track_id.toString();
        currentByTrack[trackId] = (currentByTrack[trackId] || 0) + value;
      }
    });

    const futureIndex = currentIntervalIndex + 6;
    const futureByTrack: Record<string, number> = {};
    let futureSystemTotal = 0;

    trackZones.forEach((zone) => {
      const value = zone.values[futureIndex] || 0;
      futureSystemTotal += value;

      if (zone.apm_track_id) {
        const trackId = zone.apm_track_id.toString();
        futureByTrack[trackId] = (futureByTrack[trackId] || 0) + value;
      }
    });

    const percentChange =
      currentSystemTotal > 0
        ? ((futureSystemTotal - currentSystemTotal) / currentSystemTotal) * 100
        : 0;

    const trackBreakdown = tracks.map((track) => ({
      trackId: track.id,
      trackCode: track.code,
      currentPax: currentByTrack[track.id] || 0,
      futurePax: futureByTrack[track.id] || 0,
    }));

    return {
      currentSystemTotal,
      futureSystemTotal,
      percentChange,
      futureTime: times[futureIndex] || "—",
      trackBreakdown,
    };
  }, [trackZones, tracks, currentIntervalIndex, times]);

  return (
    <div className="bg-slate-800 border-2 border-slate-600 rounded-lg p-4 shadow-xl min-w-[220px]">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        System Load
      </div>

      <div className="text-2xl font-bold text-white mb-3">
        {legendData.currentSystemTotal.toLocaleString()} PAX
      </div>

      <div className="space-y-1 mb-3 pb-3 border-b border-slate-600">
        {legendData.trackBreakdown.map((track) => (
          <div
            key={track.trackId}
            className="flex items-center justify-between text-sm"
          >
            <span className="text-slate-300">{track.trackCode}:</span>
            <span className="text-white font-medium">
              {track.currentPax.toLocaleString()} PAX
            </span>
          </div>
        ))}
      </div>

      <div className="text-xs text-slate-300 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-slate-400">At {legendData.futureTime}:</span>
          <span className="font-semibold">
            {legendData.futureSystemTotal.toLocaleString()}
          </span>
        </div>

        <div
          className={`text-sm font-bold ${
            legendData.percentChange >= 0 ? "text-red-400" : "text-green-400"
          }`}
        >
          {legendData.percentChange >= 0 ? "+" : ""}
          {legendData.percentChange.toFixed(1)}%
        </div>
      </div>
    </div>
  );
}
